import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Instagram, Music } from "lucide-react";
import SocialEmbed from "./SocialEmbed";

export default function SocialFeed() {
  const [posts, setPosts] = useState([]);
  const [mode, setMode] = useState("live"); // "live" = real embeds, "thumbs" = old thumbnail grid

  useEffect(() => {
    api.get("/social-posts").then((r) => setPosts(r.data.slice(0, 6))).catch(() => {});
  }, []);

  if (posts.length === 0) return null;

  return (
    <section data-testid="social-feed" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
      <div className="text-center mb-10">
        <div className="uppercase text-xs tracking-[0.4em] text-[#5C5547]">As seen on</div>
        <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl gold-heading">Latest posts.</h2>
        <div className="mt-6 mx-auto h-px w-24 gold-line" />
        <div className="mt-6 inline-flex items-center gap-1 p-1 rounded-full bg-[#F5F2EA] border border-[#EAE4D3]" data-testid="social-feed-toggle">
          <button
            onClick={() => setMode("live")}
            data-testid="social-mode-live"
            className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.2em] ${mode === "live" ? "bg-[#2C2924] text-[#FDFBF7]" : "text-[#5C5547]"}`}
          >
            Live posts
          </button>
          <button
            onClick={() => setMode("thumbs")}
            data-testid="social-mode-thumbs"
            className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.2em] ${mode === "thumbs" ? "bg-[#2C2924] text-[#FDFBF7]" : "text-[#5C5547]"}`}
          >
            Grid
          </button>
        </div>
      </div>

      {mode === "live" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="social-feed-live">
          {posts.map((p, i) => (
            <div
              key={p.id}
              className="fade-up flex justify-center thick-gold rounded-3xl p-3 bg-[#FDFBF7]"
              style={{ animationDelay: `${0.05 * i}s` }}
            >
              <SocialEmbed post={p} />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-testid="social-feed-thumbs">
          {posts.map((p, i) => {
            const Icon = p.platform === "tiktok" ? Music : Instagram;
            return (
              <a
                key={p.id}
                href={p.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`social-${p.id}`}
                className="group relative fade-up aspect-square rounded-2xl overflow-hidden block thick-gold"
                style={{ animationDelay: `${0.05 * i}s` }}
              >
                {p.image_url && (
                  <img
                    src={p.image_url}
                    alt={p.caption || ""}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-90" />
                <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-[var(--accent-strong)] flex items-center justify-center">
                  <Icon className="w-4 h-4 text-black" />
                </div>
                <div className="absolute inset-x-3 bottom-3 text-white">
                  {p.handle && <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--accent)]">{p.handle}</div>}
                  {p.caption && <div className="text-xs leading-snug line-clamp-2 mt-1">{p.caption}</div>}
                </div>
              </a>
            );
          })}
        </div>
      )}
    </section>
  );
}
